import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../i18n';
import { AdminCopy } from '../pages/AdminSection';
import logo from '../assets/logo.jpg';
import { db, subscribeToContactMessages, supabase } from '@siaedts/shared/lib/supabase';

const items = [
  ['dashboard', 'Dashboard', 'dashboard'], ['about', 'About Us', 'news'], ['products', 'Products', 'products'], ['news', 'News', 'news'],
  ['gallery', 'Gallery', 'gallery'], ['partners', 'Partners', 'partners'], ['team', 'Team', 'team'],
  ['inquiries', 'Inquiries', 'messages'], ['messages', 'Messages', 'messages'], ['feedback', 'Feedback', 'messages'], ['users', 'Users', 'team'],
] as const;

function NavigationIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    dashboard: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
    products: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    news: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2M9 5h6M7 16h6M7 8h6v4H7V8z',
    gallery: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    partners: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    team: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    messages: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 3.582-8 9-8s9 3.582 9 8z',
  };
  return <svg className="nav-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={paths[name]} /></svg>;
}

export function AdminLayout() {
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    let active = true;
    const refreshUnreadMessages = async () => {
      try {
        const messages = await db.contactMessages.getAll({ status: 'unread' });
        if (active) setUnreadMessages(messages.length);
      } catch {
        if (active) setUnreadMessages(0);
      }
    };

    void refreshUnreadMessages();
    const pollTimer = window.setInterval(() => { void refreshUnreadMessages(); }, 5000);
    let channel: ReturnType<typeof subscribeToContactMessages> | null = null;
    try {
      channel = subscribeToContactMessages(() => { void refreshUnreadMessages(); });
    } catch {
      channel = null;
    }
    return () => {
      active = false;
      window.clearInterval(pollTimer);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [user]);

  if (loading) return <div className="auth-loading">Loading admin workspace...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const current = items.find(([path]) => location.pathname.startsWith(`/admin/${path}`));
  return (
    <div className="admin-app">
      <aside className="sidebar">
        <div className="brand-block"><img className="brand-logo" src={logo} alt="SIAEDTS logo" /><span>SIAEDTS Admin</span></div>
        <nav aria-label="Admin navigation">
          {items.map(([path, label, icon]) => (
            <Link className={`nav-item ${current?.[0] === path ? 'active' : ''}`} key={path} to={`/admin/${path}`}>
              <span className="nav-icon"><NavigationIcon name={icon} /></span><span>{t(path as Parameters<typeof t>[0])}</span>{path === 'messages' && unreadMessages > 0 && <span className="nav-badge" aria-label={`${unreadMessages} unread messages`}>{unreadMessages > 99 ? '99+' : unreadMessages}</span>}
            </Link>
          ))}
        </nav>
        <div className="sidebar-user">
          <div className="avatar">{user.name.slice(0, 1).toUpperCase()}</div>
          <div><strong>{user.name}</strong><span>{user.email}</span></div>
          <button className="logout-button" type="button" onClick={() => void logout()} title={t('signOut')}>
            <svg className="logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4m-5-4l4-4m0 0l-4-4m4 4H3" /></svg>
            <span>{t('signOut')}</span>
          </button>
        </div>
      </aside>
      <div className="main-area">
        <header className="topbar">
          <div><p className="eyebrow">{t('operations')}</p><h1>{current ? t(current[0] as Parameters<typeof t>[0]) : t('dashboard')}</h1></div>
          <div className="topbar-actions">
            <span className={`connection-pill ${isOnline ? 'online' : 'offline'}`} role="status" aria-live="polite">
              <span className="status-dot" aria-hidden="true" />
              {isOnline ? t('online') : t('offline')}
            </span>
            <button type="button" className="language-toggle" onClick={toggleLanguage} aria-label={t('language')}>
              {language === 'en' ? 'FR' : 'EN'}
            </button>
            <a href="http://127.0.0.1:3000/" target="_blank" rel="noreferrer" className="view-site">{t('viewSite')}</a>
          </div>
        </header>
        <main className="legacy-main"><AdminCopy><Outlet /></AdminCopy></main>
      </div>
    </div>
  );
}
